import React, { useState, useEffect } from "react";
import {
  Empty,
  Table,
  Tooltip,
  Popconfirm,
  message,
  Tabs,
  Modal,
  Form,
  Upload,
  Segmented,
  Drawer,
  Spin,
  Image,
  Space,
  Pagination,
  Select, 
  Radio
} from "antd";
import { Input } from "components/shared/FormComponent";
import { Button } from "components/shared/ButtonComponent";
import { DeleteFilled } from "components/shared/AntIcons";
import { EyeIcon } from "assets/img/custom-icons";
import SearchIcon from "assets/img/search.svg";
import "assets/styles/table.scss";
import "assets/styles/account.scss";
import { getAllAccount } from 'services/actions/commonServiceAction';
import { ArchiveIcon, DownloadIcon, UploadIcon, EditIcon } from 'assets/img/custom-icons';

import {
  InboxOutlined,
  CloudUploadOutlined,
  ShareAltOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  BarsOutlined,
  CalendarOutlined,
  HddOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  LinkOutlined,
  VideoCameraOutlined,
  LoadingOutlined,
  EditOutlined,
} from "@ant-design/icons";

import { useDispatch, useSelector } from "react-redux";
import {
  saveResource,
  resourceDetails,
  deleteResource,
  shareResource,
  resourceDetailsPagenation,
  downloadResource,
  resourceupdate,
} from "services/actions/resourceActoin";
import { resetSaveResourceState } from "services/slices/resourceSlice";

const { Dragger } = Upload;

interface ResourceItem {
  id: number;
  guid: string;
  title: string;
  description: string;
  type: "File" | "Video" | "Others" | string;
  fileSize: string;
  date: string;
  preview: string;
  resourceUrl?: string;
  is_public?: boolean; 
  accountid?: string | number;
  account_name?: string[];
  rawItem?: any;
}

interface ApiResourceItem {
  id: number;
  guid: string;
  owner_id: number;
  title: string;
  description: string;
  resource_type: string;
  file_name: string;
  original_file_name: string;
  file_path: string;
  resource_url: string | null;
  mime_type: string;
  file_size: number;
  is_active: boolean;
  created_on: string;
  modified_on: string;
  accountid: string | number;
  is_public?: boolean;
  account_name?: string[];
}

const mapResourceType = (type: string = "") => {
  const lower = (type || "").toLowerCase().trim();

  if (
    lower === "file" ||
    lower === "document" ||
    lower === "doc" ||
    lower === "pdf" ||
    lower === "image"
  ) {
    return "File";
  }

  if (lower === "link" || lower === "video") {
    return "Video";
  }

  if (lower === "others" || lower === "other") {
    return "Others";
  }

  return "Others";
};

const getApiResourceType = (filterType: string = "") => {
  switch (filterType) {
    case "Video":
      return "link";
    case "File":
      return "document";
    case "Others":
      return "others";
    default:
      return "";
  }
};

const formatFileSize = (bytes: number) => {
  if (!bytes || bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const formatDate = (isoString: string) => {
  if (!isoString) return "---";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const getTypeBadgeStyle = (type: string = "") => {
  const normalized = mapResourceType(type);

  switch (normalized) {
    case "File":
      return { bg: "#e0e7ff", text: "#4338ca", border: "#c7d2fe" };
    case "Video":
      return { bg: "#ede9fe", text: "#7c3aed", border: "#ddd6fe" };
    case "Others":
      return { bg: "#e0f2fe", text: "#0284c7", border: "#bae6fd" };
    default:
      return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" };
  }
};

const getTypeConfig = (type: string = "") => {
  const normalized = mapResourceType(type);

  switch (normalized) {
    case "File":
      return {
        icon: <FileTextOutlined style={{ fontSize: 22, color: "#4338ca" }} />,
        bg: "#e0e7ff",
      };
    case "Video":
      return {
        icon: <PlayCircleOutlined style={{ fontSize: 22, color: "#7c3aed" }} />,
        bg: "#ede9fe",
      };
    case "Others":
    default:
      return {
        icon: <LinkOutlined style={{ fontSize: 22, color: "#0284c7" }} />,
        bg: "#e0f2fe",
      };
  }
};

const ResourceHub: React.FC = () => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "table">("table");
  const [statusFilter, setStatusFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingGuid, setDownloadingGuid] = useState<string | null>(null);
  const [sharingGuid, setSharingGuid] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();

  const selectedType = Form.useWatch("type", form) || "File";

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTableVal, setsearchTableval] = useState("");
  const userRole = sessionStorage.getItem('role');
  const storedAccountId = sessionStorage.getItem('accountid');
  const hasUserInfo = storedAccountId ? Number(storedAccountId) : 0;
  const [isImageLoading, setIsImageLoading] = useState(true);

  const {
    saveResourceLoading,
    saveResourceError,
    saveResourceSuccess,
    resourceDetailsLoading,
    resourceDetailsInfo,
    resourceDetailsPagenationLoading,
    resourceDetailsPagenationInfo,
    resourceReUploadLoading,
    resourceReUploadSuccess,
    resourceReUploadError,
  } = useSelector((state: any) => state.resource || {});
  const { allAccountInfo, loading4 } = useSelector((state: any) => state.commonData || {});

  const getResourceListWithParams = (
    search = "",
    currentPage = 1,
    size = 10,
    sortByColumn = "id",
    sortBy = "desc",
    currentFilter = statusFilter
  ) => {
    dispatch(
      resourceDetailsPagenation({
        guid: "",
        accountid: hasUserInfo || 0,
        DataFinder: {
          searchdata: search,
          sortbycolumn: sortByColumn,
          sortby: sortBy,
          currentpage: currentPage,
          pagesize: size,
          resource_type: getApiResourceType(currentFilter),
        },
      }) as any
    );
  };

  useEffect(() => {
    dispatch(getAllAccount() as any);
  }, [dispatch]);

  const accOptions: any = [
    { label: 'All', value: 0, key: 0 },
    ...(allAccountInfo?.data?.map((acc: any) => ({
      label: acc?.acct_name,
      value: acc.id,
      key: acc.id,
    })) || [])
  ];

  // Pagination, sorting அல்லது statusFilter மாறும் போது தானாக data fetch ஆகும்
  useEffect(() => {
    getResourceListWithParams(
      searchTableVal,
      pageIndex,
      pageSize,
      sortField || "id",
      sortField ? sortOrder : "desc",
      statusFilter
    );
  }, [pageIndex, pageSize, sortField, sortOrder, statusFilter]);

  const paginatedResources: ApiResourceItem[] =
    resourceDetailsPagenationInfo?.ResourceDetail || [];
  const resourceTotalRecords =
    resourceDetailsPagenationInfo?.DataFinder?.totalrecords || 0;
  const resourceTypeCounts =
    resourceDetailsPagenationInfo?.ResourceTypes || {};

  const paginatedResourceList: ResourceItem[] = paginatedResources.map(
    (item: any) => {
      const mappedType = mapResourceType(item.resource_type);
      const fullPath =
        item.file_path ||
        item.resource_filepath ||
        item.file_url ||
        item.resource_url ||
        "";

      return {
        id: item.id,
        guid: item.guid,
        title: item.title || item.file_name,
        description: item.description || "",
        type: mappedType,
        fileSize: formatFileSize(item.file_size),
        date: formatDate(item.modified_on || item.created_on),
        preview: fullPath,
        resourceUrl: fullPath,
        is_public: item.is_public !== undefined ? item.is_public : true,
        accountid: item.accountid,
        account_name: item.account_name || [],
        rawItem: item,
      };
    }
  );

  useEffect(() => {
    dispatch(resetSaveResourceState());
    return () => {
      dispatch(resetSaveResourceState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (saveResourceSuccess) {
      message.success("Resource uploaded successfully!");
      setIsModalOpen(false);
      setEditingItem(null);
      form.resetFields();
      setPageIndex(1);
      getResourceListWithParams(
        searchTableVal,
        1,
        pageSize,
        sortField,
        sortOrder,
        statusFilter
      );
      dispatch(resetSaveResourceState() as any);
    }
  }, [saveResourceSuccess, form, dispatch]);

  useEffect(() => {
    if (saveResourceError) {
      const errorMsg =
        typeof saveResourceError === "string"
          ? saveResourceError
          : saveResourceError?.data?.message ||
            "Failed to save resource. Please try again.";
      message.error(errorMsg);
      dispatch(resetSaveResourceState() as any);
    }
  }, [saveResourceError, dispatch]);

  useEffect(() => {
    if (resourceReUploadSuccess) {
      message.success("Resource updated successfully!");
      setIsModalOpen(false);
      setEditingItem(null);
      form.resetFields();
      getResourceListWithParams(
        searchTableVal,
        pageIndex,
        pageSize,
        sortField,
        sortOrder,
        statusFilter
      );
      dispatch(resetSaveResourceState() as any);
    }
  }, [resourceReUploadSuccess, form, dispatch, pageIndex, pageSize, sortField, sortOrder, statusFilter, searchTableVal]);

  // Update (resourceupdate) Error
  useEffect(() => {
    if (resourceReUploadError) {
      const errorMsg =
        typeof resourceReUploadError === "string"
          ? resourceReUploadError
          : resourceReUploadError?.data?.message ||
            resourceReUploadError?.message ||
            "Failed to update resource. Please try again.";
      message.error(errorMsg);
      dispatch(resetSaveResourceState() as any);
    }
  }, [resourceReUploadError, dispatch]);

  const handleBeforeUpload = (file: File): boolean => {
    const isLt50MB = file.size / 1024 / 1024 < 50;
    if (!isLt50MB) {
      message.error("File size must be smaller than 50MB!");
      return Upload.LIST_IGNORE as unknown as boolean;
    }
    return false;
  };

  const handleViewDetails = (record: ResourceItem) => {
    setSelected(record?.rawItem || record);
    setIsImageLoading(true);
    setDrawerOpen(true);

    if (record.guid) {
      const cleanGuid = record.guid.replace(/-/g, "");
      dispatch(resourceDetails({ guid: cleanGuid }) as any);
    }
  };

  const handleEdit = (record: ResourceItem) => {
    const raw = record?.rawItem || {};
    setEditingItem(record);

    const typeNormalized = mapResourceType(raw.resource_type || record.type);
    const isPublic = raw.is_public !== undefined ? raw.is_public : true;

    let accountIds: number[] = [0];

    if (raw.accountid !== undefined && raw.accountid !== null && raw.accountid !== "") {
      if (typeof raw.accountid === "string") {
        accountIds = raw.accountid
          .split(",")
          .map((id: string) => Number(id.trim()))
          .filter((id: number) => !isNaN(id));
      } else if (Array.isArray(raw.accountid)) {
        accountIds = raw.accountid.map((id: any) => Number(id));
      } else {
        accountIds = [Number(raw.accountid)];
      }
    }

    if (accountIds.length === 0) {
      accountIds = [0];
    }

    const existingFilePath = raw.file_path || raw.resource_filepath || record.preview || "";
    const existingUrl = raw.resource_url || record.resourceUrl || "";

    form.setFieldsValue({
      title: raw.title || record.title || "",
      description: raw.description || record.description || "",
      type: typeNormalized,
      access_type: isPublic ? "public" : "private",
      account_id: isPublic ? accountIds : [0],
      resource_url: existingUrl || (typeNormalized !== "File" ? existingFilePath : ""),
      file: (raw.file_name || raw.original_file_name)
        ? [
            {
              uid: "-1",
              name: raw.original_file_name || raw.file_name || "Existing File",
              status: "done",
              url: existingFilePath,
            },
          ]
        : undefined,
    });

    setIsModalOpen(true);
  };

  const handleDownloadResource = async (item: ResourceItem) => {
    if (item.type === "Others" || item.type === "Video") {
      if (item.resourceUrl) {
        window.open(item.resourceUrl, "_blank");
      } else {
        message.warning("URL link not found");
      }
      return;
    }

    if (!item.guid) {
      message.error("GUID not found for downloading this file");
      return;
    }

    const cleanGuid = item.guid.replace(/-/g, "");
    setDownloadingGuid(item.guid);

    try {
      const res: any = await dispatch(downloadResource(cleanGuid) as any);

      const payloadData =
        res?.payload?.data?.data ||
        res?.payload?.data ||
        res?.payload ||
        res?.data ||
        {};

      const downloadUrl = payloadData?.resource_filepath;
      const downloadName = payloadData?.file_name || item.title || "downloaded-file";

      if (!downloadUrl) {
        message.error("File download URL not found in API response");
        return;
      }

      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error("Network error");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const downloadAnchor = document.createElement("a");
        downloadAnchor.href = blobUrl;
        downloadAnchor.download = downloadName;
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch {
        const downloadAnchor = document.createElement("a");
        downloadAnchor.href = downloadUrl;
        downloadAnchor.setAttribute("download", downloadName);
        downloadAnchor.setAttribute("target", "_blank");
        downloadAnchor.setAttribute("rel", "noopener noreferrer");
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to download file");
    } finally {
      setDownloadingGuid(null);
    }
  };

  const handleSubmit = (values: any) => {
    const rawType = (values.type || "file").toLowerCase();
    const isLinkBased = rawType === "video" || rawType === "others" || rawType === "other";

    let formattedResourceType = "document";
    if (rawType === "others" || rawType === "other") {
      formattedResourceType = "others";
    } else if (rawType === "video") {
      formattedResourceType = "link";
    } else {
      formattedResourceType = "document";
    }

    const isPublic = values.access_type !== "private";
    let formattedAccountIds: number[] = [0];

    if (isPublic) {
      const selectedAccs = Array.isArray(values.account_id)
        ? values.account_id
        : [values.account_id];

      if (!selectedAccs.includes(0) && selectedAccs.length > 0) {
        formattedAccountIds = selectedAccs
          .filter((id: any) => id !== undefined && id !== null && id !== "")
          .map((id: any) => Number(id));
      }
    }

    const rawFile = values.file?.[0];
    const fileToUpload = rawFile?.originFileObj;
    const hasNewFile = fileToUpload instanceof File || fileToUpload instanceof Blob;

    const formData = new FormData();

    if (editingItem) {
      const cleanGuid = (editingItem?.guid || "").replace(/-/g, "");

      const updateInputJson = {
        guid: cleanGuid,
        title: values.title,
        description: values.description || "",
        is_public: isPublic ? "True" : "False",
        resource_url: isLinkBased ? (values.resource_url || null) : null,
        accountid: formattedAccountIds,
      };

      if (!isLinkBased && hasNewFile) {
        formData.append(
          "file",
          fileToUpload,
          (fileToUpload as File).name || "uploaded_file"
        );
      }

      formData.append("inputjson", JSON.stringify(updateInputJson));
      dispatch(resourceupdate(formData) as any);
      return;
    }

    const inputjson = {
      title: values.title,
      description: values.description || "",
      resource_type: formattedResourceType,
      resource_url: isLinkBased ? values.resource_url : "",
      is_public: isPublic,           
      accountid: formattedAccountIds, 
    };

    if (!isLinkBased) {
      if (hasNewFile) {
        formData.append(
          "file",
          fileToUpload,
          (fileToUpload as File).name || "uploaded_file"
        );
      } else {
        message.error("Please select a valid file.");
        return;
      }
    }

    formData.append("inputjson", JSON.stringify(inputjson));
    dispatch(saveResource(formData) as any);
  };

  const handleDelete = async (rawGuid: string) => {
    if (!rawGuid) {
      message.error("GUID not found for this resource");
      return;
    }

    const cleanGuid = rawGuid.replace(/-/g, "");
    try {
      const res: any = await dispatch(
        deleteResource({ guid: cleanGuid }) as any
      );
      if (res?.error || res?.type?.endsWith("/rejected")) {
        message.error(
          res?.payload?.message ||
            res?.error?.message ||
            "Failed to delete resource"
        );
      } else {
        message.success("Resource deleted successfully");
        setDrawerOpen(false);
        setPageIndex(1);
        getResourceListWithParams(
          searchTableVal,
          1,
          pageSize,
          sortField,
          sortOrder,
          statusFilter
        );
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to delete resource");
    }
  };

  const handleShareResource = async (rawGuid: string) => {
    if (!rawGuid) {
      message.error("GUID not found for this resource");
      return;
    }

    const cleanGuid = rawGuid.replace(/-/g, "");
    setSharingGuid(rawGuid);

    try {
      const res: any = await dispatch(
        shareResource({ guid: cleanGuid, expiration_hours: 24 }) as any
      );

      const resData = res?.payload?.data || res?.data || res?.payload || {};
      const token = resData?.resourceid || cleanGuid;
      const targetUrl = resData?.file_path || resData?.resource_url || resData?.file_url || "";

      if (!targetUrl) {
        message.error("File or link URL not found for sharing");
        return;
      }

      const urlValue = `${window.origin}/resource-viewer/${token}`;
      await navigator.clipboard.writeText(urlValue);
      message.success("Share link copied successfully!");
    } catch (error: any) {
      message.error(error?.message || "Failed to share resource");
    } finally {
      setSharingGuid(null);
    }
  };

  const handleSearch = (e: any) => {
    if (e.key === "Enter") {
      const searchValue = e.target.value.trim();
      setsearchTableval(searchValue);
      setPageIndex(1);
      getResourceListWithParams(
        searchValue,
        1,
        pageSize,
        sortField,
        sortOrder,
        statusFilter
      );
    }
  };

  const resetSearch = (e: any) => {
    if (e.key === "Backspace" && !e.target.value) {
      setsearchTableval("");
      setPageIndex(1);
      getResourceListWithParams("", 1, pageSize, sortField, sortOrder, statusFilter);
    }
  };

  const searchbyBtn = () => {
    setPageIndex(1);
    getResourceListWithParams(
      searchTableVal,
      1,
      pageSize,
      sortField,
      sortOrder,
      statusFilter
    );
  };

  const clearSearch = () => {
    setsearchTableval("");
    setPageIndex(1);
    getResourceListWithParams("", 1, pageSize, sortField, sortOrder, statusFilter);
  };

  const tableChange = (pagination: any, filters: any, sorter: any) => {
    let sort = "desc";
    let sortfield = "id";

    if (sorter?.order === "ascend") {
      sort = "asc";
      sortfield = sorter.field || "id";
    } else if (sorter?.order === "descend") {
      sort = "desc";
      sortfield = sorter.field || "id";
    }

    setPageIndex(pagination.current);
    setPageSize(pagination.pageSize);
    setSortField(sortfield);
    setSortOrder(sort);
  };

  const customLocale = {
    emptyText: <Empty className="p-2" description="No resources available" />,
  };

  const STATUS_FILTERS = [
    { label: "All", value: "" },
    { label: "File", value: "File" },
    { label: "Video", value: "Video" },
    { label: "Others", value: "Others" },
  ];

  const tabItems = STATUS_FILTERS.map((f) => {
    let count = 0;
    if (f.value === "") {
      count =
        (resourceTypeCounts?.files || 0) +
        (resourceTypeCounts?.videos || 0) +
        (resourceTypeCounts?.others || 0) ||
        resourceTotalRecords;
    } else if (f.value === "File") {
      count = resourceTypeCounts?.files ?? 0;
    } else if (f.value === "Video") {
      count = resourceTypeCounts?.videos ?? 0;
    } else if (f.value === "Others") {
      count = resourceTypeCounts?.others ?? 0;
    }

    return {
      key: f.value,
      label: (
        <span className="py-1 rounded-pill d-inline-block transition-all">
          {f.label} ({count})
        </span>
      ),
      children: null,
    };
  });

  const columns: any[] = [
    {
      title: "S.no",
      dataIndex: "sno",
      key: "sno",
      render: (_id: number, _record: any, index: number) => {
        return pageIndex === 1
          ? index + 1
          : (pageIndex - 1) * pageSize + (index + 1);
      },
    },
    {
      title: "Name",
      dataIndex: "title",
      key: "title",
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (text: string) => <span>{text || "--"}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string) =>
        description ? (
          <Tooltip title={description}>
            <span className="desc-cell">{description}</span>
          </Tooltip>
        ) : (
          <span className="desc-cell desc-empty">-</span>
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "center",
      width: 120,
      render: (type: string) => {
        const displayType = mapResourceType(type);
        const currentStyle = getTypeBadgeStyle(displayType);

        return (
          <div className="status-section mx-auto d-flex justify-content-center">
            <span
              className="status-badge"
              style={{
                backgroundColor: currentStyle.bg,
                color: currentStyle.text,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "80px",
                height: "26px",
                borderRadius: 4,
                fontSize: "12px",
                fontWeight: 700,
                textTransform: "capitalize",
                whiteSpace: "nowrap",
              }}
            >
              {displayType}
            </span>
          </div>
        );
      },
    },
    ...(userRole !== 'staff'
      ? [
          {
            title: "Access",
            dataIndex: "is_public",
            key: "is_public",
            align: "center" as const,
            width: 110,
            render: (is_public: boolean) => {
              const isPub = is_public !== false;
              return (
                <div className="d-flex justify-content-center">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: 700,
                      backgroundColor: isPub ? "#eff6ff" : "#fffbeb",
                      color: isPub ? "#1d4ed8" : "#b45309",
                      border: isPub ? "1px solid #bfdbfe" : "1px solid #fde68a",
                    }}
                  >
                    {isPub ? "Public" : "Private"}
                  </span>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Date Modified",
      dataIndex: "date",
      key: "date",
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (date: string) => <span>{date || "---"}</span>,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size: string, record: ResourceItem) => (
        <span>
          {record.type === "Video"
            ? "URL Stream"
            : record.type === "Others"
            ? "Web Link"
            : size || "---"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      className: "action-column",
      render: (_id: any, record: ResourceItem) => {
        const isCurrentlyDownloading = downloadingGuid === record.guid;
        const isCurrentlySharing = sharingGuid === record.guid;
        return (
          <div
            className="text-center d-flex justify-content-center align-items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <Tooltip title="View">
              <span
                className="icon-btn icon-btn-view"
                onClick={() => handleViewDetails(record)}
              >
                <EyeIcon />
              </span>
            </Tooltip>

            {userRole !== 'staff' && (
              <Tooltip title="Edit" className="mt-0 mb-1">
                <span className="edit-icon icon-edit text-success pointer pb-0"
                  onClick={() => handleEdit(record)}>
                  <EditIcon />
                </span>
              </Tooltip>
            )}

            <Tooltip title={record.type === "Others" || record.type === "Video" ? "Open Link" : "Download"}>
              <span className="px-2 fs-16 download" 
                onClick={() => handleDownloadResource(record)}
              >
                {isCurrentlyDownloading ? (
                  <LoadingOutlined className="fs-16 text-primary" />
                ) : record.type === "Others" || record.type === "Video" ? (
                  <LinkOutlined className="fs-16 text-primary pointer" />
                ) : (
                  <DownloadIcon />
                )}
              </span>
            </Tooltip>

            {/* <Tooltip title="Share">
              <span onClick={() => handleShareResource(record.guid)}>
                {isCurrentlySharing ? (
                  <LoadingOutlined className="fs-16 text-primary" />
                ) : (
                  <ShareAltOutlined className="fs-16 text-primary pointer" />
                )}
              </span>
            </Tooltip> */}

            {userRole !== 'staff' && (
              <Popconfirm
                placement="topLeft"
                title="Are you sure to delete this resource?"
                onConfirm={() => handleDelete(record.guid)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete" className="mt-0">
                  <span className="icon-btn icon-btn-delete">
                    <DeleteFilled className="fs-18 text-danger delete-icon" />
                  </span>
                </Tooltip>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-2">
      <div className="d-flex align-items-center mb-2">
        <h5 className="my-auto fw-bold text-dark">Resource Hub</h5>
      </div>

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <div className="custom-tabs m-0">
          <Tabs
            items={tabItems}
            activeKey={statusFilter}
            tabBarStyle={{ marginBottom: 0 }}
            onChange={(key) => {
              setStatusFilter(key);
              setPageIndex(1);
            }}
            indicator={{ size: 0 }}
          />
        </div>

        <div>
          {userRole !== 'staff' && (
            <Tooltip title="Add New Resource">
              <Button
                type="primary"
                onClick={() => {
                  setEditingItem(null);
                  form.resetFields();
                  setIsModalOpen(true);
                }}
                icon={<CloudUploadOutlined />}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                Upload Asset
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="my-2 p-3 bg-white border rounded">
        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
          <h6 className="my-auto fw-600 fs-16 text-dark">
            List of {statusFilter || "All"} Resources
          </h6>
          <div className="position-relative d-flex align-items-center gap-2">
            <div className="position-relative d-flex align-items-center">
              <Input
                prefix={
                  <img
                    src={SearchIcon}
                    height="14px"
                    onClick={searchbyBtn}
                    className="pointer"
                    alt="search"
                  />
                }
                onKeyDown={(e) => handleSearch(e)}
                value={searchTableVal}
                onChange={(e) =>
                  setsearchTableval(e.target.value.replace(/^\s+/, ""))
                }
                onKeyUp={(e) => resetSearch(e)}
                className="search-input col px-2 rounded fs-14"
                placeholder="Search by Title..."
                style={{ width: "250px" }}
              />
              {searchTableVal && (
                <button
                  type="button"
                  className="search-clear-btn position-absolute end-0 me-2 border-0 bg-transparent"
                  onClick={clearSearch}
                  style={{ zIndex: 5 }}
                >
                  ×
                </button>
              )}
            </div>

            {/* <Segmented
              value={viewMode}
              onChange={(val) => setViewMode(val as "grid" | "table")}
              options={[
                { value: "table", icon: <BarsOutlined /> },
                { value: "grid", icon: <AppstoreOutlined /> },
              ]}
              style={{ background: "#e2e8f0", padding: 2, borderRadius: 8 }}
            /> */}
          </div>
        </div>

        {viewMode === "table" ? (
          <Table
            className="pointer"
            rowKey="id"
            columns={columns}
            dataSource={paginatedResourceList}
            loading={resourceDetailsPagenationLoading}
            locale={customLocale}
            scroll={{ x: "calc(230px + 50%)" }}
            onChange={tableChange}
            onRow={(record: any) => ({
              onClick: () => handleViewDetails(record),
            })}
            pagination={{
              current: pageIndex,
              pageSize: pageSize,
              total: resourceTotalRecords,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              onChange: (page, size) => {
                setPageIndex(page);
                setPageSize(size);
              },
            }}
          />
        ) : (
          <Spin spinning={resourceDetailsPagenationLoading}>
            {paginatedResourceList.length === 0 ? (
              <Empty className="p-4 my-4" description="No resources available" />
            ) : (
              <>
                <div
                  style={{
                    minHeight: 250,
                    paddingRight: 6,
                    paddingBottom: 12,
                  }}
                >
                  <div className="row g-3">
                    {paginatedResourceList.map((item) => {
                      const config = getTypeConfig(item.type);
                      const isItemDownloading = downloadingGuid === item.guid;
                      const isItemSharing = sharingGuid === item.guid;

                      const isImage =
                        item.rawItem?.mime_type?.startsWith("image/") ||
                        /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(item.preview);

                      return (
                        <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={item.id}>
                          <div
                            className="card h-100 border p-3 pointer shadow-sm transition-all"
                            style={{ borderRadius: 12 }}
                            onClick={() => handleViewDetails(item)}
                          >
                            {isImage && item.preview ? (
                              <div
                                className="mb-3 rounded overflow-hidden position-relative d-flex align-items-center justify-content-center"
                                style={{
                                  height: 140,
                                  backgroundColor: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                }}
                                onClick={(e) => e.stopPropagation()} 
                              >
                                <Image
                                  src={item.preview}
                                  alt={item.title}
                                  style={{
                                    height: 140,
                                    width: "100%",
                                    objectFit: "cover",
                                  }}
                                  fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='%2394a3b8' viewBox='0 0 16 16'><path d='M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z'/><path d='M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z'/></svg>"
                                  placeholder={
                                    <div
                                      className="d-flex align-items-center justify-content-center w-100 h-100"
                                      style={{ height: 140, backgroundColor: "#f1f5f9" }}
                                    >
                                      <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                    </div>
                                  }
                                />
                              </div>
                            ) : null}

                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="rounded d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: config.bg,
                                  }}
                                >
                                  {config.icon}
                                </div>

                                {userRole !== 'staff' && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 700,
                                      padding: "2px 8px",
                                      borderRadius: "6px",
                                      backgroundColor: item.is_public ? "#eff6ff" : "#fffbeb",
                                      color: item.is_public ? "#1d4ed8" : "#b45309",
                                      border: item.is_public ? "1px solid #bfdbfe" : "1px solid #fde68a",
                                    }}
                                  >
                                    {item.is_public ? "Public" : "Private"}
                                  </span>
                                )}
                              </div>

                              <div
                                className="d-flex align-items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {userRole !== 'staff' && (
                                  <Tooltip title="Edit Resource">
                                    <span
                                      className="p-1 rounded pointer d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        color: "#d97706",
                                        backgroundColor: "#fef3c7",
                                        border: "1px solid #fde68a",
                                        width: 28,
                                        height: 28,
                                        borderRadius: 6,
                                      }}
                                      onClick={() => handleEdit(item)}
                                    >
                                      <EditOutlined style={{ fontSize: 14 }} />
                                    </span>
                                  </Tooltip>
                                )}

                                <Tooltip
                                  title={
                                    item.type === "Others" || item.type === "Video"
                                      ? "Open Link"
                                      : "Download File"
                                  }
                                >
                                  <span
                                    className="p-1 rounded pointer d-inline-flex align-items-center justify-content-center"
                                    style={{
                                      color:
                                        item.type === "Others"
                                          ? "#0284c7"
                                          : item.type === "Video"
                                          ? "#7c3aed"
                                          : "#16a34a",
                                      backgroundColor:
                                        item.type === "Others"
                                          ? "#e0f2fe"
                                          : item.type === "Video"
                                          ? "#ede9fe"
                                          : "#f0fdf4",
                                      border:
                                        item.type === "Others"
                                          ? "1px solid #bae6fd"
                                          : item.type === "Video"
                                          ? "1px solid #ddd6fe"
                                          : "1px solid #bbf7d0",
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                    }}
                                    onClick={() => handleDownloadResource(item)}
                                  >
                                    {isItemDownloading ? (
                                      <LoadingOutlined style={{ fontSize: 14 }} />
                                    ) : item.type === "Others" || item.type === "Video" ? (
                                      <LinkOutlined style={{ fontSize: 14 }} />
                                    ) : (
                                      <DownloadOutlined style={{ fontSize: 14 }} />
                                    )}
                                  </span>
                                </Tooltip>

                                {userRole !== 'staff' && (
                                  <Tooltip title="Share Resource">
                                    <span
                                      className="p-1 rounded pointer d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        color: "#4f46e5",
                                        backgroundColor: "#eef2ff",
                                        border: "1px solid #c7d2fe",
                                        width: 28,
                                        height: 28,
                                        borderRadius: 6,
                                      }}
                                      onClick={() => handleShareResource(item.guid)}
                                    >
                                      {isItemSharing ? (
                                        <LoadingOutlined style={{ fontSize: 14 }} />
                                      ) : (
                                        <ShareAltOutlined style={{ fontSize: 14 }} />
                                      )}
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                            </div>

                            <h6 className="fw-bold mb-1 text-truncate">{item.title}</h6>
                            <p
                              className="text-secondary small mb-3 text-truncate"
                              style={{ minHeight: 18 }}
                            >
                              {item.description || "-"}
                            </p>

                            <div className="mt-auto pt-2 border-top d-flex justify-content-between text-muted small">
                              <span>
                                <CalendarOutlined className="me-1" />
                                {item.date}
                              </span>
                              <span className="fw-semibold">
                                <HddOutlined className="me-1" />
                                {item.type === "Video"
                                  ? "URL Stream"
                                  : item.type === "Others"
                                  ? "Web Link"
                                  : item.fileSize}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="d-flex justify-content-end align-items-center mt-3 pt-2 border-top">
                  <Pagination
                    current={pageIndex}
                    pageSize={pageSize}
                    total={resourceTotalRecords}
                    showSizeChanger={true}
                    pageSizeOptions={["5", "10", "20", "50", "100"]}
                    onChange={(page, size) => {
                      setPageIndex(page);
                      setPageSize(size);
                    }}
                  />
                </div>
              </>
            )}
          </Spin>
        )}
      </div>

      <Drawer
        title={<span style={{ fontWeight: 700 }}>Resource Details</span>}
        placement="right"
        width={460}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        <Spin spinning={resourceDetailsLoading}>
          {(() => {
            const apiData = resourceDetailsInfo?.data || resourceDetailsInfo;
            const detail = (apiData && Object.keys(apiData).length > 0) ? apiData : selected;

            if (!detail) {
              return (
                <Empty
                  description="No details available"
                  style={{ marginTop: 60 }}
                />
              );
            }

            const rawType = detail.resource_type || detail.type || "";
            const normalizedType = mapResourceType(rawType);
            const config = getTypeConfig(normalizedType);
            const formattedDate = formatDate(
              detail.modified_on || detail.created_on || detail.date
            );
            const formattedSize = detail.file_size
              ? formatFileSize(detail.file_size)
              : detail.fileSize || "---";

            const targetPreviewUrl =
              detail.file_path ||
              detail.resource_url ||
              detail.preview ||
              detail.resourceUrl ||
              "";

            const fileNameDisplay =
              detail.original_file_name ||
              detail.file_name ||
              detail.title ||
              "File";

            const isActive =
              detail.is_active !== undefined ? detail.is_active : true;

            const isImageFile =
              detail.mime_type?.startsWith("image/") ||
              /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(targetPreviewUrl);

            const accNames: string[] = Array.isArray(detail.account_name)
              ? detail.account_name
              : selected?.account_name || [];

            const isAccNotZero =
              detail.accountid !== 0 &&
              detail.accountid !== "0" &&
              detail.accountid !== undefined &&
              detail.accountid !== null;

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div
                  style={{
                    height: 220,
                    width: "100%",
                    backgroundColor: "#f8fafc",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {normalizedType === "Video" ? (
                    <div className="text-center p-3">
                      <PlayCircleOutlined style={{ fontSize: 44, color: "#7c3aed" }} />
                      <div
                        className="mt-2 small text-muted text-truncate"
                        style={{ maxWidth: 350 }}
                      >
                        {targetPreviewUrl || "Video Link"}
                      </div>
                      {targetPreviewUrl && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => window.open(targetPreviewUrl, "_blank")}
                        >
                          Watch Video
                        </Button>
                      )}
                    </div>
                  ) : normalizedType === "Others" ? (
                    <div className="text-center p-3">
                      <LinkOutlined style={{ fontSize: 44, color: "#0284c7" }} />
                      <div
                        className="mt-2 small text-muted text-truncate"
                        style={{ maxWidth: 350 }}
                      >
                        {targetPreviewUrl || "External Resource Link"}
                      </div>
                    </div>
                  ) : isImageFile && targetPreviewUrl ? (
                    <div
                      className="position-relative w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{ minHeight: 210 }}
                    >
                      {isImageLoading && (
                        <div
                          className="position-absolute d-flex flex-column align-items-center justify-content-center w-100 h-100"
                          style={{
                            inset: 0,
                            backgroundColor: "#f8fafc",
                            zIndex: 2,
                          }}
                        >
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
                            tip={<div className="mt-2 text-muted small">Loading Preview...</div>}
                          />
                        </div>
                      )}

                      <Image
                        src={targetPreviewUrl}
                        alt="Preview"
                        onLoad={() => setIsImageLoading(false)}
                        onError={() => setIsImageLoading(false)}
                        style={{
                          maxHeight: 210,
                          maxWidth: "100%",
                          objectFit: "contain",
                          display: isImageLoading ? "none" : "block",
                        }}
                      />
                    </div>
                  ) : (
                    <div className="text-center p-3">
                      <div className="mb-2">{config.icon}</div>
                      <div className="small text-muted mb-2 text-truncate" style={{ maxWidth: 350 }}>
                        {fileNameDisplay}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h5 className="mb-1 text-dark">{detail.title || fileNameDisplay}</h5>
                  <Space size={6}>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: getTypeBadgeStyle(normalizedType).bg,
                        color: getTypeBadgeStyle(normalizedType).text,
                        padding: "5px 10px",
                        fontWeight: 700,
                      }}
                    >
                      {normalizedType}
                    </span>
                    {userRole !== 'staff' && (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: detail.is_public !== false ? "#eff6ff" : "#fffbeb",
                          color: detail.is_public !== false ? "#1d4ed8" : "#b45309",
                          border: detail.is_public !== false ? "1px solid #bfdbfe" : "1px solid #fde68a",
                          padding: "5px 10px",
                          fontWeight: 700,
                        }}
                      >
                        {detail.is_public !== false ? "Public" : "Private"}
                      </span>
                    )}
                    {detail.mime_type && (
                      <span className="badge bg-light text-dark border">
                        {detail.mime_type}
                      </span>
                    )}
                  </Space>
                </div>

                <div>
                  <h6 className="text-dark fw-bold mb-1">Overview</h6>
                  <p className="text-muted small mb-0">
                    {detail.description || "No description provided."}
                  </p>
                </div>

                <hr className="my-1" />

                <div>
                  <h6 className="text-dark fw-bold mb-2">Data Properties</h6>
                  <div className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">File Name</span>
                    <span className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>
                      {fileNameDisplay}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">File Size</span>
                    <span className="fw-semibold">
                      {normalizedType === "Video"
                        ? "URL Stream"
                        : normalizedType === "Others"
                        ? "Web Link"
                        : formattedSize}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">Date Modified</span>
                    <span className="fw-semibold">{formattedDate}</span>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Status</span>
                    <span
                      className={`badge ${
                        isActive ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {userRole !== 'staff' && detail.is_public !== false && (isAccNotZero || accNames.length > 0) && (
                    <div className="d-flex justify-content-between align-items-start mt-2 mb-2 small">
                      <span className="text-muted">Account Names</span>
                      <div className="d-flex flex-wrap gap-1 justify-content-end" style={{ maxWidth: 240 }}>
                        {accNames.length > 0 ? (
                          accNames.map((name: string, index: number) => (
                            <span
                              key={index}
                              className="badge"
                              style={{
                                backgroundColor: "#f1f5f9",
                                color: "#334155",
                                border: "1px solid #cbd5e1",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="fw-semibold text-dark">
                            {detail.accountid || "---"}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </Spin>
      </Drawer>

      {/* Modal */}
      <Modal
        title={<span className="fw-bold">{editingItem ? "Edit Resource Asset" : "Upload New Resource Asset"}</span>}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingItem(null);
          form.resetFields();
        }}
        footer={null}
        centered
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 16 }}
          initialValues={{
            type: "File",
            access_type: "public", 
            account_id: [0],        
          }}
        >
          <Form.Item
            name="title"
            label={<span className="fw-semibold">Title</span>}
            rules={[{ required: true, message: "Please provide a resource title" }]}
          >
            <Input placeholder="e.g., Clinical Protocol Reference" />
          </Form.Item>

          <Form.Item
            name="access_type"
            label={<span className="fw-semibold">Access Type</span>}
          >
            <Radio.Group
              onChange={(e) => {
                if (e.target.value === "private") {
                  form.setFieldValue("account_id", [0]);
                }
              }}
            >
              <Radio value="public">Public</Radio>
              <Radio value="private">Private</Radio>
            </Radio.Group>
          </Form.Item>
      
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.access_type !== currentValues.access_type
            }
          >
            {({ getFieldValue }) => {
              const isPrivate = getFieldValue("access_type") === "private";
      
              return (
                <Form.Item
                  name="account_id"
                  label={<span className="fw-semibold">Select Account</span>}
                  rules={
                    isPrivate
                      ? []
                      : [{ required: true, message: "Please select an account" }]
                  }
                >
                  <Select
                    mode="multiple"
                    disabled={isPrivate} 
                    placeholder="Choose an account"
                    showSearch
                    options={accOptions}
                    filterOption={(input: any, option: any) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(selectedValues: any[]) => {
                      if (selectedValues.includes(0)) {
                        if (selectedValues[selectedValues.length - 1] === 0) {
                          form.setFieldValue("account_id", [0]);
                        } else {
                          form.setFieldValue(
                            "account_id",
                            selectedValues.filter((val) => val !== 0)
                          );
                        }
                      }
                    }}
                    notFoundContent={
                      <div className="text-center p-4">
                        {loading4 ? (
                          <span>
                            <LoadingOutlined /> Loading...
                          </span>
                        ) : (
                          <span>No account found</span>
                        )}
                      </div>
                    }
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
      
          <Form.Item
            name="type"
            label={<span className="fw-semibold">Type</span>}
            rules={[{ required: true, message: "Please select resource type" }]}
          >
            <Segmented block options={["File", "Video", "Others"]} />
          </Form.Item>
      
          <Form.Item
            name="description"
            label={<span className="fw-semibold">Description</span>}
          >
            <Input.TextArea
              rows={3}
              placeholder="Provide instructions or clinical reference notes..."
            />
          </Form.Item>

          {selectedType === "Video" || selectedType === "Others" ? (
            <Form.Item
              name="resource_url"
              label={
                <span className="fw-semibold">
                  {selectedType === "Video" ? "Video Streaming Link" : "Resource / Website Link"}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: `Please enter the ${selectedType === "Video" ? "video" : "resource"} URL`,
                },
                { type: "url", message: "Please enter a valid URL (e.g., https://...)" },
              ]}
            >
              <Input
                prefix={
                  selectedType === "Video" ? (
                    <VideoCameraOutlined style={{ color: "#7c3aed" }} />
                  ) : (
                    <LinkOutlined style={{ color: "#0284c7" }} />
                  )
                }
                placeholder={
                  selectedType === "Video"
                    ? "Paste YouTube, Vimeo, Google Drive video or MP4 link..."
                    : "Paste Google Drive, Dropbox, Notion, or any Web link..."
                }
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="file"
              label={<span className="fw-semibold">File Attachment</span>}
              valuePropName="fileList"
              getValueFromEvent={(e: any) => {
                if (Array.isArray(e)) return e.slice(-1);
                return e?.fileList?.slice(-1);
              }}
              rules={[{ required: !editingItem, message: "File attachment is required" }]}
            >
              <Dragger
                beforeUpload={handleBeforeUpload}
                multiple={false}
                maxCount={1}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/*"
                style={{ borderRadius: 8, background: "#fafafa" }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: "#2563eb" }} />
                </p>
                <p className="ant-upload-text" style={{ fontSize: 14 }}>
                  Click or drag file here {editingItem ? "to replace existing file" : ""}
                </p>
                <p className="ant-upload-hint text-muted" style={{ fontSize: 12 }}>
                  Supports PDF, DOC, Excel, Images, etc. up to 50MB
                </p>
              </Dragger>
            </Form.Item>
          )}

          <div className="text-end mt-4">
            <Button
              onClick={() => {
                setIsModalOpen(false);
                setEditingItem(null);
                form.resetFields();
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={editingItem ? resourceReUploadLoading : saveResourceLoading}
            >
              {editingItem ? "Update Resource" : "Upload Resource"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ResourceHub;